import React, { useState } from "react";
import Download from "../icons/Downloadicon";
import * as XLSX from "xlsx";
import { getAlpha2Code } from "../lib/helper/countryToAphlaCode";
import { toast } from "react-toastify";
import { exportInvoicesExcel } from "../utils/exportInvoicesExcel";

const getInvoiceCurrencySymbol = (currency) => {
  return String(currency || "USD").toUpperCase() === "EUR" ? "EUR" : "USD";
};

const formatInvoiceAmount = (amount, currency) => {
  const numericAmount = Number(amount);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: getInvoiceCurrencySymbol(currency),
    minimumFractionDigits: 2,
  }).format(Number.isNaN(numericAmount) ? 0 : numericAmount);
};

const ExportCSVPopup = ({ closePopup, Data, columnsToExport, exportName, userDetails }) => {
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const handleClosePopup = (e) => {
    if (e.target.id === "popup-overlay") {
      closePopup();
    }
  };

  const handleExport = async () => {
    const rawData = Data || [];

    if (rawData.length === 0) {
      toast.warning("No records found to export.");
      return;
    }

    // Sort export records by due date ascending (earliest due date first)
    const filteredData = [...rawData].sort((a, b) => {
      const timeA = a?.dueDate ? new Date(a.dueDate).getTime() : 0;
      const timeB = b?.dueDate ? new Date(b.dueDate).getTime() : 0;
      const validA = Number.isNaN(timeA) ? 0 : timeA;
      const validB = Number.isNaN(timeB) ? 0 : timeB;
      if (!validA && !validB) return 0;
      if (!validA) return 1;
      if (!validB) return -1;
      return validA - validB;
    });

    const today = new Date().setHours(0, 0, 0, 0);

    const dataToExport = filteredData.map((quote) => {
      let row = {};
      columnsToExport?.forEach((col) => {
        if (col.field === "assignTask") {
          row[col.label] = quote.assignAdmin || "Not Assigned";
        } else if (col.field === "shipTo") {
          const loadCountryCode = quote.loadCountry
            ? getAlpha2Code(quote.loadCountry)
            : "N/A";
          const unloadCountryCode = quote.unloadCountry
            ? getAlpha2Code(quote.unloadCountry)
            : "N/A";
          row[col.label] = `${loadCountryCode} - ${unloadCountryCode}`;
        }
        else if (col.field === "orderDetails") {
          row[col.label] = quote.orderDetails?.orderDate || "N/A";
        }
        else if (col.field === "sendStatus") {
          row[col.label] = quote.sendStatus ? "Yes" : "No";
        }
        else if (col.label === "User Name" || col.field === "userName") {
          row[col.label] = quote.name || userDetails?.[quote.userId]?.name || "N/A";
        }
        else if (col.label === "Invoice Date" || col.field === "invoiceDate") {
          const invDate = quote.date || quote.invoiceDate || quote.createdAt;
          row[col.label] = invDate ? new Date(invDate).toLocaleDateString() : "N/A";
        }
        else if (col.label === "Due Date" || col.field === "dueDate") {
          row[col.label] = quote.dueDate ? new Date(quote.dueDate).toLocaleDateString() : "N/A";
        }
        else if (col.label === "Days Overdue") {
          const dueDate = quote.dueDate ? new Date(quote.dueDate).setHours(0, 0, 0, 0) : null;
          row[col.label] = dueDate && dueDate < today ? Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)) : "0";
        }
        else if (exportName === "Invoice" && col.label === "Invoice Amount") {
          row[col.label] = formatInvoiceAmount(quote.total, quote.currency);
        }
        else if (exportName === "Invoice" && col.label === "Amount Paid") {
          row[col.label] = formatInvoiceAmount(quote.paid, quote.currency);
        }
        else if (exportName === "Invoice" && col.label === "Outstanding Amount") {
          row[col.label] = formatInvoiceAmount(quote.remaining, quote.currency);
        }
        else {
          row[col.label] = quote[col.field] ?? "N/A";
        }
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ExportedData");
    const filename = `${exportName}_Export.xlsx`;
    XLSX.writeFile(workbook, filename);
    closePopup();
  };

  const handleExportExcel = async () => {
    const rawData = Data || [];

    if (rawData.length === 0) {
      toast.warning("No records found to export.");
      return;
    }

    // Sort export records by due date ascending (earliest due date first)
    const filteredData = [...rawData].sort((a, b) => {
      const timeA = a?.dueDate ? new Date(a.dueDate).getTime() : 0;
      const timeB = b?.dueDate ? new Date(b.dueDate).getTime() : 0;
      const validA = Number.isNaN(timeA) ? 0 : timeA;
      const validB = Number.isNaN(timeB) ? 0 : timeB;
      if (!validA && !validB) return 0;
      if (!validA) return 1;
      if (!validB) return -1;
      return validA - validB;
    });

    setIsExportingExcel(true);
    try {
      const fname = `${exportName}_Export.xlsx`;

      await exportInvoicesExcel({
        invoices: filteredData,
        userDetails: userDetails || {},
        filename: fname,
        companyName: "Filtered Data",
      });
      toast.success("Excel exported successfully!");
      closePopup();
    } catch (err) {
      console.error("Excel export error:", err);
      toast.error("Failed to export Excel: " + (err?.message || "Unknown error"));
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div
      id="popup-overlay"
      className="fixed inset-0 z-50 bg-opacity-50 bg-gray-800 flex justify-center items-center"
      onClick={handleClosePopup}
    >
      <div className="bg-white p-6 rounded-md w-[460px] max-w-[92vw] relative shadow-xl">
        <button
          onClick={closePopup}
          className="absolute top-4 right-5 text-gray-400 text-2xl hover:text-gray-600 transition-colors cursor-pointer"
        >
          &times;
        </button>
        <h3 className="text-xl font-semibold mb-2 text-gray-900">
          Export {exportName === "Invoice" ? "Invoices" : exportName || "Data"}
        </h3>
        <p className="text-gray-600 text-sm mb-6 leading-relaxed">
          Exporting <span className="font-bold text-gray-900">{Data?.length || 0}</span> {exportName === "Invoice" ? "invoice(s)" : "record(s)"} currently displayed in the table.
        </p>

        {/* Export Action Buttons */}
        <div className="flex items-center justify-center gap-3 pt-4 border-t border-gray-100">
          <button
            className="btnBg text-white py-2 px-4 rounded-[5px] text-sm flex items-center justify-center gap-2 font-medium whitespace-nowrap shrink-0 hover:opacity-90 transition-opacity cursor-pointer shadow-sm"
            onClick={handleExport}
          >
            <Download />
            <span>Export CSV</span>
          </button>
          {exportName === "Invoice" && (
            <button
              className="btnBg text-white py-2 px-4 rounded-[5px] text-sm flex items-center justify-center gap-2 font-medium whitespace-nowrap shrink-0 disabled:opacity-60 hover:opacity-90 transition-opacity cursor-pointer shadow-sm"
              onClick={handleExportExcel}
              disabled={isExportingExcel}
            >
              <Download />
              <span>{isExportingExcel ? "Generating..." : "Export Excel"}</span>
            </button>
          )}
          <button
            onClick={closePopup}
            className="bg-gray-100 text-gray-700 py-2 px-4 rounded-[5px] text-sm font-medium whitespace-nowrap shrink-0 hover:bg-gray-200 transition-colors cursor-pointer border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportCSVPopup;
